import { Directive, ElementRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Simple phone mask directive that formats international phone numbers.
 *
 * Features:
 * - No default country code added - user must enter complete number
 * - Display format: "+XX NNNNNNNNNN" (country code + space + digits)
 * - Saves without spaces: "+XXNNNNNNNNNN" (digits only, no spaces)
 * - Mobile-friendly with inputmode="tel"
 *
 * Usage:
 * ```html
 * <input appPhoneMask formControlName="phone" placeholder="+55 11999998888" />
 * ```
 */
@Directive({
  selector: '[appPhoneMask]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PhoneMaskDirective,
      multi: true,
    },
  ],
  host: {
    '(input)': 'onInput($event)',
    '(blur)': 'onTouched()',
    '[attr.inputmode]': '"tel"',
    '[attr.autocomplete]': '"tel"',
  },
})
export class PhoneMaskDirective implements ControlValueAccessor {
  private readonly _el = inject(ElementRef);
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: () => void = () => {};

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    // Keep only digits and +
    const raw = input.value.replace(/[^\d+]/g, '');

    // Format display: if starts with +, add space after country code (2-3 digits after +)
    let formatted = raw;
    if (raw.startsWith('+') && raw.length > 3) {
      // Find where country code ends (typically 2-3 digits after +)
      // Common patterns: +1, +55, +351, etc.
      const countryCodeMatch = raw.match(/^\+\d{1,3}/);
      if (countryCodeMatch) {
        const countryCode = countryCodeMatch[0];
        const rest = raw.substring(countryCode.length);
        formatted = `${countryCode} ${rest}`;
      }
    }

    // Update display
    input.value = formatted;

    // Position cursor at end
    requestAnimationFrame(() => {
      const pos = formatted.length;
      input.setSelectionRange(pos, pos);
    });

    // Emit without spaces for API storage
    const valueForApi = raw;
    this._onChange(valueForApi);
  }

  writeValue(value: string): void {
    if (value) {
      // Format for display: add space after country code
      const raw = value.replace(/[^\d+]/g, '');
      let formatted = raw;

      if (raw.startsWith('+') && raw.length > 3) {
        const countryCodeMatch = raw.match(/^\+\d{1,3}/);
        if (countryCodeMatch) {
          const countryCode = countryCodeMatch[0];
          const rest = raw.substring(countryCode.length);
          formatted = `${countryCode} ${rest}`;
        }
      }

      this._el.nativeElement.value = formatted;
    } else {
      this._el.nativeElement.value = '';
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._el.nativeElement.disabled = isDisabled;
  }

  onTouched(): void {
    this._onTouched();
  }
}
